import { useEffect, useState } from "react";
import { getFeedHealth, type FeedHealth } from "./actions";
import { downloadCsv } from "./csv";

// Feed Health: every log of the feed pipeline in one place, one tab per log, each with a CSV export. The watcher asks each vendor for each watched file's
// size and time (nothing is downloaded) and starts an ingest only when a file differs from the version last processed.
//   Vendor files      the current state of each file and when the watcher will look at it next
//   Watcher ticks     every tick that checked a feed, started a job or hit a problem (plus a heartbeat for the quiet ones)
//   Failures          every time the watcher itself failed
//   Change history    every change seen in a vendor file (the gaps are each vendor's real update cadence)
//   Ingest log        what each ingest found and wrote
//   Image fetches     what each image fetch run fetched, skipped and found wrong
// INTERNAL; exports download a CSV only.

const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const toCsv = (head: string[], lines: unknown[][]) => [head, ...lines].map((l) => l.map(csvCell).join(",")).join("\r\n");
const short = (iso: string | null | undefined) => (iso ? iso.replace("T", " ").replace("Z", " UTC").slice(0, 20) : "—");
const mb = (n: number | null) => (n == null ? "—" : `${(n / 1e6).toFixed(1)} MB`);
const ago = (iso: string | null | undefined, now: string) => {
  if (!iso) return "—";
  const m = Math.round((new Date(now).getTime() - new Date(iso).getTime()) / 60000);
  return m < 90 ? `${m} min ago` : m < 2880 ? `${(m / 60).toFixed(1)} h ago` : `${(m / 1440).toFixed(1)} d ago`;
};
const lag = (s: number) => (s < 5400 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);
type Tab = "files" | "ticks" | "failures" | "history" | "ingests" | "images";
const TABS: { key: Tab; label: string }[] = [
  { key: "files", label: "Vendor files" }, { key: "ticks", label: "Watcher ticks" }, { key: "failures", label: "Failures" },
  { key: "history", label: "Change history" }, { key: "ingests", label: "Ingest log" }, { key: "images", label: "Image fetches" },
];

export function FeedHealthPanel() {
  const [data, setData] = useState<FeedHealth | null>(null);
  const [tab, setTab] = useState<Tab>("files");
  const [feed, setFeed] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFeedHealth().then((r) => { setLoading(false); if (r.ok) setData(r.data); else setError(r.error); });
  }, []);

  const now = data?.now ?? "";
  const keep = <T extends { feed?: string | null }>(rows: T[] | undefined) => (rows ?? []).filter((r) => !feed || !r.feed || r.feed === feed);
  const files = keep(data?.files);
  const history = keep(data?.history);
  const ingests = keep(data?.changes);
  const failures = keep(data?.failures);
  const images = keep(data?.imageRuns);
  const ticks = (data?.ticks ?? []).filter((t) => !feed || t.checked.includes(feed) || t.skipped_not_due.includes(feed) || Object.keys(t.errors).includes(feed));
  const feeds = Array.from(new Set((data?.files ?? []).map((f) => f.feed))).sort();
  const bad = files.filter((f) => f.health !== "green").length;
  const beat = data?.heartbeat;

  const exports: Record<Tab, { name: string; head: string[]; rows: unknown[][] }> = {
    files: { name: "hec-feed-files.csv", head: ["feed", "file", "kind", "health", "vendor size", "vendor last modified", "first seen", "last checked", "last changed seen",
      "checks since change", "processed version", "last ingest", "learned gap (min)", "regular cadence", "changes observed", "check mode", "next check"],
      rows: files.map((f) => [f.feed, f.path, f.kind, f.health, f.vendor_size, f.vendor_modified, f.first_seen_at, f.last_checked_at, f.last_changed_at, f.unchanged_checks,
        f.ingested_modified, f.last_ingest_status, f.learnedGapMinutes, f.learnedRegular, f.changesObserved, f.checkMode, f.nextCheckAt]) },
    ticks: { name: "hec-watcher-ticks.csv", head: ["when", "seconds", "checked", "skipped (not due)", "started", "held back", "errors", "changes seen", "failures logged"],
      rows: ticks.map((t) => [t.at, t.seconds, t.checked.join(" "), t.skipped_not_due.join(" "), Object.keys(t.started).join(" "), JSON.stringify(t.held), JSON.stringify(t.errors), t.changes, t.failures_logged]) },
    failures: { name: "hec-watcher-failures.csv", head: ["when", "kind", "feed", "job", "message"], rows: failures.map((x) => [x.at, x.kind, x.feed, x.job, x.message]) },
    history: { name: "hec-feed-change-history.csv", head: ["feed", "file", "kind", "vendor size", "vendor modified", "previous modified", "detected at", "lag (s)", "gap since previous (s)"],
      rows: history.map((h) => [h.feed, h.path, h.kind, h.vendor_size, h.vendor_modified, h.previous_modified, h.detected_at, h.lag_seconds, h.gap_seconds]) },
    ingests: { name: "hec-feed-ingest-log.csv", head: ["feed", "job", "mode", "trigger", "status", "started", "finished", "seconds", "new", "changed", "unchanged", "gone", "written", "updated", "field changes", "note"],
      rows: ingests.map((c) => [c.feed, c.job, c.mode, c.trigger, c.status, c.started_at, c.finished_at, c.seconds, c.plan?.new, c.plan?.changed, c.plan?.unchanged, c.plan?.gone,
        c.written, c.updated, JSON.stringify(c.plan?.field_changes ?? {}), c.note]) },
    images: { name: "hec-image-fetch-log.csv", head: ["feed", "started", "seconds", "records", "fetched", "stored", "with problems", "no bytes", "MB", "manifest"],
      rows: images.map((i) => [i.feed, i.started_at, i.seconds, i.records, i.fetched, i.stored, i.with_problems, i.no_bytes, i.mb, i.manifest]) },
  };
  const cur = exports[tab];

  return (
    <div className="sheet-page">
      <div className="sheet-top">
        <div className="sheet-title">
          <h1>Feed health</h1>
          <span className="meta">
            {loading ? "Loading…" : `${files.length} files${bad ? `, ${bad} need attention` : ", all healthy"}`}.
            {beat ? ` Watcher last ran ${ago(beat.last_tick_at, now)} (${beat.last_tick_seconds}s)${Object.keys(beat.last_errors || {}).length ? ", with errors" : ""}.` : !loading ? " The watcher has not run yet." : ""}
            {" "}An ingest starts only when a vendor file has changed.
          </span>
        </div>
        <div className="tabs">
          {TABS.map((t) => <button key={t.key} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>
            {t.label}{t.key === "failures" && failures.length ? ` (${failures.length})` : ""}</button>)}
          <span style={{ marginLeft: 16 }} />
          <button className={!feed ? "active" : ""} onClick={() => setFeed("")}>All feeds</button>
          {feeds.map((f) => <button key={f} className={feed === f ? "active" : ""} onClick={() => setFeed(f)}>{f}</button>)}
        </div>
        {error && <div className="panel error sheet-error">{error}</div>}
        <div className="sheet-actions">
          <button type="button" className="sheet-export" disabled={!cur.rows.length} onClick={() => downloadCsv(cur.name, toCsv(cur.head, cur.rows))}>
            Export this log ({cur.rows.length})
          </button>
        </div>
      </div>

      <div className="sheet-scroll">
        {tab === "files" && (
          <table className="manual-products sheet-table feed-table">
            <thead><tr><th>Health</th><th>Feed</th><th>File</th><th>Kind</th><th>Size</th><th>Vendor last modified</th><th>Last checked</th><th>Checks since change</th>
              <th>Processed version</th><th>Last ingest</th><th>Learned gap</th><th>Check mode</th><th>Next check</th></tr></thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.docId}>
                  <td><span className={`dot dot-${f.health === "green" ? "ok" : f.health}`} role="img" aria-label={f.health} title={f.error || f.last_ingest_note || f.health} /></td>
                  <td>{f.feed}</td><td className="mono wrap">{f.path}</td><td>{f.kind}</td><td className="num">{mb(f.vendor_size)}</td>
                  <td>{short(f.vendor_modified)} <span className="meta">({ago(f.vendor_modified, now)})</span></td>
                  <td>{ago(f.last_checked_at, now)}</td><td className="num">{f.unchanged_checks ?? "—"}</td>
                  <td>{short(f.ingested_modified)} {f.needsIngest && <span className="map-state map-not_captured">waiting</span>}</td>
                  <td>{f.last_ingest_status ?? "never"}{f.consecutive_failures ? ` (${f.consecutive_failures} failures)` : ""}</td>
                  <td className="num">{f.learnedGapMinutes == null ? `— (${f.changesObserved} seen)` : `${f.learnedGapMinutes} min${f.learnedRegular ? "" : " (irregular)"} (${f.changesObserved} seen)`}</td>
                  <td title="learning: too few changes seen yet · predicted: waiting for the expected change · irregular: no steady rhythm · late: the expected change has not appeared · backoff: the vendor refused the last check">{f.checkMode}</td>
                  <td>{f.nextCheckAt ? short(f.nextCheckAt) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "ticks" && (
          <table className="manual-products sheet-table feed-table">
            <thead><tr><th>When</th><th>Seconds</th><th>Checked</th><th>Skipped (not due)</th><th>Started</th><th>Held back</th><th>Errors</th><th>Changes seen</th></tr></thead>
            <tbody>
              {ticks.map((t) => (
                <tr key={t.docId}>
                  <td>{short(t.at)}</td><td className="num">{t.seconds}</td><td>{t.checked.join(", ") || "—"}</td><td>{t.skipped_not_due.join(", ") || "—"}</td>
                  <td className="wrap">{Object.entries(t.started_why ?? {}).map(([j, why]) => `${j}: ${why.join("; ")}`).join(" | ") || "—"}</td>
                  <td className="wrap">{Object.entries(t.held ?? {}).map(([j, why]) => `${j}: ${why}`).join(" | ") || "—"}</td>
                  <td className="wrap">{Object.entries(t.errors ?? {}).map(([k, v]) => `${k}: ${v}`).join(" | ") || "—"}</td><td className="num">{t.changes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "failures" && (
          <table className="manual-products sheet-table feed-table">
            <thead><tr><th>When</th><th>Kind</th><th>Feed</th><th>Job</th><th>What happened</th></tr></thead>
            <tbody>
              {failures.map((x) => (
                <tr key={x.docId}><td>{short(x.at)}</td><td><span className="sev sev-high">{x.kind}</span></td><td>{x.feed ?? "—"}</td><td className="mono">{x.job ?? "—"}</td><td className="wrap">{x.message}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "history" && (
          <table className="manual-products sheet-table feed-table">
            <thead><tr><th>Feed</th><th>File</th><th>Vendor modified</th><th>Detected</th><th>Lag</th><th>Gap since previous</th><th>Size</th></tr></thead>
            <tbody>
              {history.slice(0, 300).map((h) => (
                <tr key={h.docId}>
                  <td>{h.feed}</td><td className="mono wrap">{h.path}</td><td>{short(h.vendor_modified)}</td><td>{short(h.detected_at)}</td>
                  <td className="num">{lag(h.lag_seconds)}</td><td className="num">{h.gap_seconds == null ? "first seen" : lag(h.gap_seconds)}</td><td className="num">{mb(h.vendor_size)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "ingests" && (
          <table className="manual-products sheet-table feed-table">
            <thead><tr><th>Feed</th><th>Job</th><th>Started</th><th>Trigger</th><th>Status</th><th>New</th><th>Changed</th><th>Unchanged</th><th>Gone</th><th>Written</th><th>Updated</th><th>Fields changed</th><th>Seconds</th></tr></thead>
            <tbody>
              {ingests.map((c) => (
                <tr key={c.docId}>
                  <td>{c.feed}</td><td className="mono">{c.job}</td><td>{short(c.started_at)}</td><td>{c.trigger}</td><td title={c.note}>{c.status}</td>
                  <td className="num">{c.plan?.new ?? "—"}</td><td className="num">{c.plan?.changed ?? "—"}</td><td className="num">{c.plan?.unchanged ?? "—"}</td>
                  <td className="num">{c.plan?.gone ?? "—"}</td><td className="num">{c.written ?? "—"}</td><td className="num">{c.updated ?? "—"}</td>
                  <td className="wrap">{c.plan ? Object.entries(c.plan.field_changes).map(([k, v]) => `${k} ${v}`).join(", ") : c.note}</td><td className="num">{c.seconds ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "images" && (
          <table className="manual-products sheet-table feed-table">
            <thead><tr><th>Feed</th><th>Started</th><th>Seconds</th><th>Records</th><th>Fetched</th><th>Stored</th><th>With problems</th><th>No image</th><th>MB</th><th>Manifest (per-image detail)</th></tr></thead>
            <tbody>
              {images.map((i) => (
                <tr key={i.docId}>
                  <td>{i.feed}</td><td>{short(i.started_at)}</td><td className="num">{i.seconds}</td><td className="num">{i.records}</td><td className="num">{i.fetched}</td>
                  <td className="num">{i.stored}</td><td className="num">{i.with_problems}</td><td className="num">{i.no_bytes}</td><td className="num">{i.mb}</td><td className="mono wrap">{i.manifest}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {cur.rows.length === 0 && !loading && !error && <p className="meta" style={{ padding: 16 }}>Nothing in this log yet.</p>}
      </div>
    </div>
  );
}
